/**
 * Tabs Component
 *
 * Modern tabs component with underline indicator and animated tab panels.
 *
 * @package PresszoneComments
 */

import { el } from '../utils/dom.js';

// Track tab indices for direction calculation
const tabIndexMap = new WeakMap();

/**
 * Create a tabs container.
 * @param {Object} options
 * @param {Array} options.tabs - Array of { id, label, icon?, count? }
 * @param {string} options.activeTab - Currently active tab ID
 * @param {Function} options.onChange - Callback when tab changes
 * @param {Function} [options.onChangeWithDirection] - Callback with (id, direction) for animated panels
 * @returns {HTMLElement}
 */
export function Tabs({ tabs, activeTab, onChange, onChangeWithDirection }) {
    const container = el('div', { class: 'presszone-comments-tabs', role: 'tablist' });

    // Build index map for direction calculation
    const indexMap = {};
    tabs.forEach((tab, idx) => {
        indexMap[tab.id] = idx;
    });
    tabIndexMap.set(container, { indexMap, currentIndex: indexMap[activeTab] || 0 });

    tabs.forEach(tab => {
        const isActive = tab.id === activeTab;
        const tabEl = el('button', {
            type: 'button',
            role: 'tab',
            id: `tab-${tab.id}`,
            'aria-selected': isActive ? 'true' : 'false',
            'aria-controls': `panel-${tab.id}`,
            class: `presszone-comments-tabs__item ${isActive ? 'presszone-comments-tabs__item--active' : ''}`,
            'data-tab': tab.id,
            onclick: (e) => {
                e.preventDefault();
                // Check active state from DOM to support external updates (partial re-renders)
                const isCurrentlyActive = tabEl.getAttribute('aria-selected') === 'true';
                
                if (!isCurrentlyActive) {
                    const data = tabIndexMap.get(container);
                    const newIndex = data.indexMap[tab.id];
                    const direction = newIndex > data.currentIndex ? 'right' : 'left';
                    data.currentIndex = newIndex;

                    if (onChangeWithDirection) {
                        onChangeWithDirection(tab.id, direction);
                    }
                    onChange?.(tab.id);
                }
            }
        },
            tab.icon ? el('span', { class: 'presszone-comments-tabs__icon' }, tab.icon) : null,
            el('span', { class: 'presszone-comments-tabs__label' }, tab.label),
            tab.count !== undefined ? el('span', { class: 'presszone-comments-tabs__count' }, String(tab.count)) : null
        );
        container.appendChild(tabEl);
    });

    return container;
}

/**
 * Render tabs inside a modal header.
 * @param {Array} tabs - Array of { id, label }
 * @param {string} activeTab - Currently active tab ID
 * @param {Function} onChange - Callback when tab changes
 * @param {Function} [options.onChangeWithDirection] - Callback with (id, direction)
 * @returns {HTMLElement}
 */
export function ModalTabs({ tabs, activeTab, onChange, onChangeWithDirection }) {
    const container = el('div', { class: 'presszone-comments-tabs presszone-comments-tabs--modal', role: 'tablist' });

    const indexMap = {};
    tabs.forEach((tab, idx) => {
        indexMap[tab.id] = idx;
    });
    tabIndexMap.set(container, { indexMap, currentIndex: indexMap[activeTab] || 0 });

    tabs.forEach(tab => {
        const isActive = tab.id === activeTab;
        const tabEl = el('button', {
            type: 'button',
            role: 'tab',
            id: `tab-${tab.id}`,
            'aria-selected': isActive ? 'true' : 'false',
            'aria-controls': `panel-${tab.id}`,
            class: `presszone-comments-tabs__item ${isActive ? 'presszone-comments-tabs__item--active' : ''}`,
            'data-tab': tab.id,
            onclick: (e) => {
                e.preventDefault();
                // Check active state from DOM to support external updates (partial re-renders)
                const isCurrentlyActive = tabEl.getAttribute('aria-selected') === 'true';
                
                if (!isCurrentlyActive) {
                    const data = tabIndexMap.get(container);
                    const newIndex = data.indexMap[tab.id];
                    const direction = newIndex > data.currentIndex ? 'right' : 'left';
                    data.currentIndex = newIndex;

                    if (onChangeWithDirection) {
                        onChangeWithDirection(tab.id, direction);
                    }
                    onChange?.(tab.id);
                }
            }
        }, tab.label);
        container.appendChild(tabEl);
    });

    return container;
}

/**
 * Animated tab panel wrapper.
 * Wraps tab content and applies slide animation when direction changes.
 *
 * @param {Object} options
 * @param {HTMLElement} options.content - The tab panel content (must be a DOM node)
 * @param {string} [options.direction] - Animation direction: 'left', 'right', or null for no animation
 * @param {string} [options.className] - Additional CSS classes
 * @param {string} [options.id] - Element ID
 * @returns {HTMLElement}
 */
export function TabPanel({ content, direction = null, className = '', id = '' }) {
    const animationClass = direction === 'right'
        ? 'presszone-comments-tab-panel--slide-right'
        : direction === 'left'
            ? 'presszone-comments-tab-panel--slide-left'
            : '';

    const panel = el('div', {
        class: `presszone-comments-tab-panel ${animationClass} ${className}`.trim(),
        role: 'tabpanel',
        id: id,
        'aria-labelledby': id ? id.replace('panel-', 'tab-') : null
    });

    // Handle content - must be a DOM node
    if (content instanceof Node) {
        panel.appendChild(content);
    }

    return panel;
}

/**
 * Create a complete tabbed interface with animated panels.
 *
 * @param {Object} options
 * @param {Array} options.tabs - Array of { id, label, icon?, count? }
 * @param {string} options.activeTab - Currently active tab ID
 * @param {Function} options.renderContent - Function(tabId) => HTMLElement to render tab content
 * @param {Function} options.onChange - Callback when tab changes (receives tab id)
 * @param {string} [options.variant] - 'default' or 'modal'
 * @param {string} [options.contentClass] - Additional class for content wrapper
 * @returns {HTMLElement}
 */
export function TabbedPanel({ tabs, activeTab, renderContent, onChange, variant = 'default', contentClass = '' }) {
    let currentTab = activeTab;
    let currentDirection = null;

    const container = el('div', { class: 'presszone-comments-tabbed-panel' });

    // Create tabs
    const TabsComponent = variant === 'modal' ? ModalTabs : Tabs;

    const updateContent = () => {
        // Remove old content
        const oldContent = container.querySelector('.presszone-comments-tabbed-panel__content');
        if (oldContent) {
            oldContent.remove();
        }

        // Create new content with animation
        const content = renderContent(currentTab);
        const contentWrapper = el('div', {
            class: `presszone-comments-tabbed-panel__content ${contentClass}`.trim()
        });

        const panel = TabPanel({
            content,
            direction: currentDirection,
            id: `panel-${currentTab}`
        });

        contentWrapper.appendChild(panel);
        container.appendChild(contentWrapper);

        // Reset direction after use
        currentDirection = null;
    };

    const handleTabChange = (tabId, direction) => {
        currentTab = tabId;
        currentDirection = direction;

        // Update tab active states
        container.querySelectorAll('.presszone-comments-tabs__item').forEach(tabEl => {
            const isActive = tabEl.dataset.tab === tabId;
            tabEl.classList.toggle('presszone-comments-tabs__item--active', isActive);
            tabEl.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        updateContent();
        onChange?.(tabId);
    };

    // Render tabs
    const tabsEl = TabsComponent({
        tabs,
        activeTab: currentTab,
        onChange: () => {}, // Handled by onChangeWithDirection
        onChangeWithDirection: handleTabChange
    });
    container.appendChild(tabsEl);

    // Initial content render (no animation)
    updateContent();

    return container;
}

export default Tabs;
